import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["handleClick", "children", "top", "layout"],
      _excluded2 = ["title", "headerColor", "handleClick", "minHeight", "onPrint", "children", "layout", "attributes", "noIcon", "hcpcs"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core/styles';
import { DialogContent, Dialog } from '@material-ui/core';
import Draggable from 'react-draggable';
import { Checkmark, Printer } from '@mummssoftware/icons';
import Paper from '@material-ui/core/Paper';
import { isSmallTouchDevice, safariDetect } from '@mummssoftware/utils/web';
import { SectionHeader } from './SectionHeader';
import { DialogProvider } from '../DialogProvider';
import { ActionIcon } from '../ActionIcon'; // @note: https://stackoverflow.com/a/54812656

const layouts = {};
layouts.default = {
  width: 620,
  height: 240
};
layouts.maximize = {
  width: 620,
  height: 240
};
layouts.minimize = {
  width: 300,
  height: 34
};
const HeaderDiv = styled.div.withConfig({
  displayName: "DraggableDialog__HeaderDiv",
  componentId: "kg1p70-0"
})(["display:inline-flex;align-content:center;align-items:center;gap:5px;"]); // the dialog gets pointer-events: none, which blocks all clicks including backdrop clicks.
// @note disableBackdropClick has no effect

const StyledDialog = styled(Dialog).withConfig({
  displayName: "DraggableDialog__StyledDialog",
  componentId: "kg1p70-1"
})(["", " & .resizable{position:relative;border:3px solid ", ";border-top:none;overflow:visible;}& .resizable > *{overflow:visible;}& .resizable > .react-resizable-handle{position:absolute;width:20px;height:20px;bottom:0;right:0;background:url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+');background-position:bottom right;padding:0 3px 3px 0;background-repeat:no-repeat;background-origin:content-box;box-sizing:border-box;cursor:se-resize;}& .MuiDialog-paper{overflow:visible;}"], props => props.disableInteraction ? '' : `&& {
    pointer-events: none;
  }`, props => props.theme.palette.error.light);
const StyledPaper = styled(Paper).withConfig({
  displayName: "DraggableDialog__StyledPaper",
  componentId: "kg1p70-2"
})(["pointer-events:auto;& .MuiDialog-paper{overflow:visible;}"]);
const StyledDiv = styled(Paper).withConfig({
  displayName: "DraggableDialog__StyledDiv",
  componentId: "kg1p70-3"
})([""]); // size of header

const DraggableDialogContent = styled(DialogContent).withConfig({
  displayName: "DraggableDialog__DraggableDialogContent",
  componentId: "kg1p70-4"
})(["overflow:scroll !important;max-height:", "px;min-height:160px;"], props => props.x);
const globPosition = {
  x: 0,
  y: 0
};

const reposition = (response, ref, callback) => {
  var _ref$current, _ref$current2;

  const height = ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight;
  const width = ref === null || ref === void 0 ? void 0 : (_ref$current2 = ref.current) === null || _ref$current2 === void 0 ? void 0 : _ref$current2.clientWidth;
  let y = 0; // globPosition.y;

  let x = 0; // globPosition.x;

  switch (response) {
    case 'bottom':
      y = 0;
      break;

    case 'top':
      y = window.innerHeight - height;
      break;

    case 'left':
      x = window.innerWidth - width;
      break;

    case 'right':
      x = 0;
      break;
  }

  globPosition.x = x;
  globPosition.y = y;
  const transform = `translate(-${x}px, -${y}px)`;
  ref.current.style.transform = transform;
};

let widgetContainerRef; // reference used in component context

function PaperComponent(props) {
  widgetContainerRef = React.useRef(null);
  const [widgetState, setWidgetState] = React.useState({
    position: {
      x: 0,
      y: 0
    },
    bounds: {
      left: 0,
      top: 0,
      bottom: 0,
      right: 0
    }
  });
  const [isInitialized, setIsInitialized] = React.useState(false); // Calculate center position after the component mounts and has dimensions

  React.useEffect(() => {
    if (widgetContainerRef.current && !isInitialized) {
      const rect = widgetContainerRef.current.getBoundingClientRect();
      const centerX = (window.innerWidth - rect.width) / 2;
      const centerY = (window.innerHeight - rect.height) / 2;

      if (!(isSmallTouchDevice() && safariDetect())) {
        globPosition.x = centerX;
        globPosition.y = centerY;
        setWidgetState(prev => _objectSpread(_objectSpread({}, prev), {}, {
          position: {
            x: centerX,
            y: centerY
          }
        }));
      }

      setIsInitialized(true);
    }
  }, [isInitialized]);
  const positionObj = isSmallTouchDevice() && safariDetect() ? {
    position: 'fixed',
    right: 0,
    top: 0
  } : {
    position: 'fixed',
    left: 0,
    top: 0
  }; // Change to top-left origin

  const onDrag = (event, uiData) => {
    globPosition.x = uiData === null || uiData === void 0 ? void 0 : uiData.x;
    globPosition.y = uiData === null || uiData === void 0 ? void 0 : uiData.y;
  };

  const onStart = (event, uiData) => {
    var _window, _window$document, _widgetContainerRef, _widgetContainerRef$c;

    const {
      clientWidth,
      clientHeight
    } = (_window = window) === null || _window === void 0 ? void 0 : (_window$document = _window.document) === null || _window$document === void 0 ? void 0 : _window$document.documentElement;
    const targetRect = (_widgetContainerRef = widgetContainerRef) === null || _widgetContainerRef === void 0 ? void 0 : (_widgetContainerRef$c = _widgetContainerRef.current) === null || _widgetContainerRef$c === void 0 ? void 0 : _widgetContainerRef$c.getBoundingClientRect();

    if (targetRect) {
      setWidgetState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
        bounds: {
          left: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.left) + (uiData === null || uiData === void 0 ? void 0 : uiData.x),
          right: clientWidth - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.right) - (uiData === null || uiData === void 0 ? void 0 : uiData.x)),
          top: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.top) + (uiData === null || uiData === void 0 ? void 0 : uiData.y),
          bottom: clientHeight - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.bottom) - (uiData === null || uiData === void 0 ? void 0 : uiData.y))
        }
      }));
    }
  };

  const onStop = (event, uiData) => {
    var _window2, _window2$document, _widgetContainerRef2, _widgetContainerRef2$;

    const {
      clientWidth,
      clientHeight
    } = (_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$document = _window2.document) === null || _window2$document === void 0 ? void 0 : _window2$document.documentElement;
    const targetRect = (_widgetContainerRef2 = widgetContainerRef) === null || _widgetContainerRef2 === void 0 ? void 0 : (_widgetContainerRef2$ = _widgetContainerRef2.current) === null || _widgetContainerRef2$ === void 0 ? void 0 : _widgetContainerRef2$.getBoundingClientRect();
    setWidgetState(prevState => _objectSpread(_objectSpread({}, prevState), {}, {
      position: {
        x: uiData.x,
        y: uiData.y
      },
      bounds: {
        left: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.left) + (uiData === null || uiData === void 0 ? void 0 : uiData.x),
        right: clientWidth - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.right) - (uiData === null || uiData === void 0 ? void 0 : uiData.x)),
        top: -(targetRect === null || targetRect === void 0 ? void 0 : targetRect.top) + (uiData === null || uiData === void 0 ? void 0 : uiData.y),
        bottom: clientHeight - ((targetRect === null || targetRect === void 0 ? void 0 : targetRect.bottom) - (uiData === null || uiData === void 0 ? void 0 : uiData.y))
      }
    }));
  };

  const update = position => {
    reposition(position, widgetContainerRef, setWidgetState);
  };

  return /*#__PURE__*/React.createElement(DialogProvider, {
    func: update,
    ref: widgetContainerRef
  }, /*#__PURE__*/React.createElement(Draggable, {
    allowAnyClick: true,
    position: widgetState.position // Use controlled position
    ,
    onDrag: (event, uiData) => onDrag(event, uiData),
    onStart: (event, uiData) => onStart(event, uiData),
    onStop: (event, uiData) => onStop(event, uiData),
    bounds: widgetState.bounds,
    handle: "#draggable-header",
    cancel: '[class*="MuiDialogContent-root"]',
    nodeRef: widgetContainerRef
  }, /*#__PURE__*/React.createElement("div", {
    style: positionObj,
    ref: widgetContainerRef
  }, /*#__PURE__*/React.createElement(StyledPaper, props))));
}

const DialogHeader = ({
  handleClick = () => null,
  onPrint,
  style,
  id,
  title,
  noIcon,
  headerColor,
  attributes,
  hcpcs
}) => {
  const [icon, setIcon] = useState('minimize');
  return /*#__PURE__*/React.createElement(SectionHeader, {
    title: title,
    style: headerColor ? _objectSpread(_objectSpread({}, style), {}, {
      backgroundColor: headerColor
    }) : style,
    id: id,
    minimized: icon === 'maximize',
    nav: /*#__PURE__*/React.createElement(HeaderDiv, null, hcpcs, attributes, onPrint && /*#__PURE__*/React.createElement(ActionIcon, {
      htmlColor: "white",
      "data-testid": "dialog-done",
      Icon: Printer,
      onClick: onPrint,
      style: {
        margin: 0
      }
    }), noIcon ? null : /*#__PURE__*/React.createElement(ActionIcon, {
      "data-testid": "dialog-done",
      Icon: Checkmark,
      onClick: handleClick,
      style: {
        margin: 0,
        color: 'white'
      }
    }))
  });
};
/* disableEnforceFocus : the modal will not prevent focus from leaving the modal while open.
Generally this should never be set to true as it makes the modal less accessible to assistive technologies, like screen readers. 
We need it for cert-app and clearscripts signed verbal tab
*/
// TODO Invalid prop children of type array supplied to resizableBox
// top to have the dialog box open on top of page instead of middle default


const DialogWrap = _ref => {
  let {
    handleClick,
    children,
    top,
    layout
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  const boxProps = {
    id: 'resize-box-drag'
  };
  const Container = /*#__PURE__*/React.createElement(StyledDialog, _extends({
    id: "dialog",
    PaperComponent: PaperComponent,
    maxWidth: false,
    "aria-labelledby": "draggable-dialog-title",
    disableEnforceFocus: true // Let the user focus on elements outside the dialog

  }, props), /*#__PURE__*/React.createElement(StyledDiv, _extends({
    style: layout === 'minimize' ? {
      overflow: 'hidden',
      maxHeight: 0.7 * window.innerHeight
    } : {
      overflow: 'visible',
      maxHeight: 0.7 * window.innerHeight
    }
  }, boxProps), /*#__PURE__*/React.createElement("div", null, children)));
  return Container;
};

export const DraggableDialog = _ref2 => {
  let {
    title,
    headerColor,
    handleClick,
    minHeight,
    onPrint,
    children,
    layout,
    attributes,
    noIcon,
    hcpcs
  } = _ref2,
      props = _objectWithoutProperties(_ref2, _excluded2);

  const theme = useTheme();
  const style = {
    cursor: 'move',
    backgroundColor: theme.palette.error.light,
    overflow: 'visible'
  };
  const x = 0.7 * window.innerHeight - 31;
  return /*#__PURE__*/React.createElement(DialogWrap, _extends({
    id: "dialog",
    handleClick: handleClick,
    layout: layout
  }, props), /*#__PURE__*/React.createElement(DialogHeader, {
    id: "draggable-dialog-title",
    title: layout !== 'minimize' ? title : '',
    headerColor: headerColor,
    style: style,
    handleClick: handleClick,
    onPrint: onPrint,
    attributes: attributes,
    hcpcs: hcpcs,
    noIcon: noIcon
  }), /*#__PURE__*/React.createElement(DraggableDialogContent, {
    x: x
  }, children));
};
export default DraggableDialog;